// ─── src/components/sections/InfoStrip.jsx ───────────────────────────────────
import { useState } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { STYLES } from "../../data/venues";

// STYLES[0] is the "all styles" sentinel — clicking an active vibe clears back to it.
const DEFAULT_STYLE = STYLES[0];

// NO invented defaults. This component used to ship a hardcoded DEFAULT_COLS of
// "Rustic Luxe" / "Michelin-Star Dining" and render every item as a role="button" with a
// pointer cursor and a gold hover — but no onClick anywhere. On /england that meant three
// columns of values with ZERO overlap with the data (England's real style values are
// Historic 40, Exclusive Use 33, Country House 21, Castle 12) attached to controls that
// did nothing. A column with nothing real behind it is now omitted rather than invented,
// and an item is only interactive when a handler exists to make it do something.

export default function InfoStrip({
  regionNames = [],
  // The shape RegionPage actually passes. The two signatures had drifted apart, so every
  // prop the caller sent was silently ignored and the invented defaults rendered instead.
  availableRegions = [],
  vibes,
  services,
  cols,
  defaultOpen = false,
  filters,
  onFiltersChange,
  onRegionSelect,
  // Value a chip returns to when toggled off. The live venue filter's sentinel is "All";
  // the legacy STYLES list uses "All Styles". Passed in rather than assumed.
  styleClearValue = DEFAULT_STYLE,
  regionClearValue = "all",
}) {
  const C = useTheme();
  const [hovered, setHovered] = useState(null);
  const [open, setOpen] = useState(defaultOpen);

  // Build columns dynamically. An item carries an optional `onSelect`; without one it
  // renders as plain text, not as something that looks pressable.
  const regionItems = availableRegions.length > 0
    ? availableRegions.map((r) => ({
        label: r.name,
        value: r.slug,
        active: filters?.region === r.slug,
        onSelect: onRegionSelect
          ? () => onRegionSelect(r.slug)
          : (onFiltersChange && filters
              ? () => onFiltersChange({ ...filters, region: filters.region === r.slug ? regionClearValue : r.slug })
              : null),
      }))
    : regionNames.map((n) => ({ label: n, value: n, active: false, onSelect: null }));

  const vibeItems = (Array.isArray(vibes) ? vibes : []).map((v) => {
    const label = typeof v === "string" ? v : v.label;
    const value = typeof v === "string" ? v : (v.value ?? v.label);
    return {
      label, value,
      active: filters?.style === value,
      onSelect: (onFiltersChange && filters)
        ? () => onFiltersChange({ ...filters, style: filters.style === value ? styleClearValue : value })
        : null,
    };
  });

  // Elite Services has no filter dimension anywhere in the model, so its items are
  // descriptive by definition. Rendered as text, and omitted entirely when not supplied.
  const serviceItems = (Array.isArray(services) ? services : [])
    .map((sv) => ({ label: typeof sv === "string" ? sv : sv.label, value: null, active: false, onSelect: null }));

  const COLS = cols || [
    ...(regionItems.length  > 0 ? [{ label: "Regions",        items: regionItems  }] : []),
    ...(vibeItems.length    > 0 ? [{ label: "Signature Vibe", items: vibeItems    }] : []),
    ...(serviceItems.length > 0 ? [{ label: "Elite Services", items: serviceItems }] : []),
  ];

  // Nothing real to show beats a panel of decorative chips.
  if (COLS.length === 0) return null;

  return (
    <div
      style={{
        background: C.dark,
        borderBottom: `1px solid ${C.border}`,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      {/* Toggle bar */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "10px 48px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: C.off || "#1a1a1a",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "2.5px",
          textTransform: "uppercase",
          fontFamily: "var(--font-body)",
          transition: "color 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = C.gold)}
        onMouseLeave={(e) => (e.currentTarget.style.color = C.off || "#1a1a1a")}
      >
        <span style={{
          display: "inline-block",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          fontSize: 10,
        }}>
          ▼
        </span>
        {open ? "Hide Details" : "Regions · Vibes · Services"}
      </button>

      {/* Collapsible content */}
      <div
        style={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          transition: "grid-template-rows 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div style={{ overflow: "hidden" }}>
          <div
            className="lwd-infostrip-outer"
            style={{
              maxWidth: 1280,
              margin: "0 auto",
              padding: "0 48px",
              display: "grid",
              gridTemplateColumns: `repeat(${COLS.length}, 1fr)`,
              opacity: open ? 1 : 0,
              transition: "opacity 0.3s ease 0.1s",
            }}
          >
            {COLS.map((col, i) => (
              <div
                key={i}
                className="lwd-infostrip-col"
                style={{
                  padding: "36px 0",
                  borderRight: i < COLS.length - 1 ? `1px solid ${C.border}` : "none",
                  paddingRight: i < COLS.length - 1 ? 48 : 0,
                  paddingLeft: i > 0 ? 48 : 0,
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                    color: C.off || "#1a1a1a",
                    fontWeight: 700,
                    marginBottom: 16,
                    fontFamily: "var(--font-body)",
                  }}
                  aria-label={col.label}
                >
                  ✦ {col.label}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {col.items.map((item, j) => {
                    const key = `${i}-${j}`;
                    const isHov = hovered === key;
                    const isOn = !!item.active;
                    const interactive = typeof item.onSelect === "function";
                    const lit = isOn || (interactive && isHov);
                    const base = {
                      fontSize: 12,
                      color: lit ? C.gold : (C.off || "#1a1a1a"),
                      background: isOn ? C.goldDim : C.card,
                      border: `1px solid ${lit ? C.gold : C.border}`,
                      padding: "5px 12px",
                      borderRadius: "var(--lwd-radius-input)",
                      fontFamily: "var(--font-body)",
                      transition: "all 0.2s",
                    };
                    // Descriptive item: no pointer, no hover, no button role. It reads as
                    // the label it is instead of promising an interaction that never came.
                    if (!interactive) {
                      return (
                        <span key={j} style={{ ...base, cursor: "default", opacity: 0.85 }}>
                          {item.label}
                        </span>
                      );
                    }
                    return (
                      <button
                        key={j}
                        type="button"
                        aria-pressed={isOn}
                        onClick={item.onSelect}
                        onMouseEnter={() => setHovered(key)}
                        onMouseLeave={() => setHovered(null)}
                        style={{ ...base, cursor: "pointer", font: "inherit", fontSize: 12 }}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
